import { formatDate, t } from "@hakili/i18n";
import { useEffect, useState } from "react";

/**
 * `round CN-C`, `CN-C.4`, WF-CON-42: **« Fonctionnalités », to the drawing
 * (`hakili-console.jsx`, `FonctionnalitesScreen`), the administration alone.**
 *
 * « Demandes détaillées » is LIVE from `GET /feature-requests` , every
 * request from every app, with who, role, organisation, source app, screen,
 * date, the three answers and the screenshot , the requests the founder's
 * own « Une idée ? » has been sending since `CN-A.0`, read at last.
 * « Consolidation Hakili » is in words with intelligence's round named and
 * the drawing's rule stated: a bucket's rank is never a pure count.
 */
export interface FeatureRequestView {
  readonly id: string;
  readonly app: string;
  readonly requesterName: string;
  readonly requesterRole: string;
  readonly organisationName: string | null;
  readonly screenLabel: string;
  readonly goal: string;
  readonly missing: string;
  readonly benefit: string;
  readonly screenshotUrl: string | null;
  readonly submittedAt: string;
}

export interface FonctionnalitesDoors {
  readonly readFeatureRequests: () => Promise<readonly FeatureRequestView[]>;
}

const DASH = t("common.valeur.tiret");

const fill = (key: Parameters<typeof t>[0], values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, value), t(key));

type Tab = "demandes" | "consolidation";

export function Fonctionnalites(props: {
  readonly requests: readonly FeatureRequestView[] | null;
  readonly refusal: string | null;
}): React.JSX.Element {
  const [tab, setTab] = useState<Tab>("demandes");
  return (
    <div data-testid="fonctionnalites">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{t("console.fonctionnalites.titre")}</h1>
        <p className="mt-1.5 text-sm text-gray-500">{t("console.fonctionnalites.sousTitre")}</p>
      </div>
      <div className="mb-4 flex items-center gap-2">
        {(
          [
            ["demandes", "console.fonctionnalites.ongletDemandes"],
            ["consolidation", "console.fonctionnalites.ongletConsolidation"],
          ] as const
        ).map(([id, key]) => (
          <button
            key={id}
            type="button"
            data-testid={`fonctionnalites-onglet-${id}`}
            onClick={() => {
              setTab(id);
            }}
            className={
              "rounded-full border px-3 py-1.5 text-xs font-medium " +
              (tab === id
                ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50")
            }
          >
            {t(key)}
          </button>
        ))}
      </div>
      {props.refusal === null ? null : (
        <p role="alert" data-testid="fonctionnalites-refus" className="mb-3 text-sm text-rose-600">
          {props.refusal}
        </p>
      )}
      {tab === "consolidation" ? (
        <div
          data-testid="fonctionnalites-consolidation"
          className="rounded-2xl border border-gray-200 bg-white px-5 py-5"
        >
          <p className="text-sm text-gray-500">{t("console.fonctionnalites.consolidationRond")}</p>
        </div>
      ) : (
        <div
          data-testid="fonctionnalites-demandes"
          className="overflow-hidden rounded-2xl border border-gray-200 bg-white"
        >
          <div className="flex items-center gap-4 border-b border-gray-100 px-5 py-2.5">
            <span className="w-48 text-xs text-gray-500">{t("console.fonctionnalites.colQui")}</span>
            <span className="w-44 text-xs text-gray-500">{t("console.fonctionnalites.colApp")}</span>
            <span className="flex-1 text-xs text-gray-500">{t("console.fonctionnalites.colFaire")}</span>
            <span className="w-28 text-xs text-gray-500">{t("console.fonctionnalites.colCapture")}</span>
            <span className="w-24 text-right text-xs text-gray-500">
              {t("console.fonctionnalites.colDate")}
            </span>
          </div>
          {props.requests === null ? (
            <p className="px-5 py-5 text-sm text-gray-500">{t("common.state.chargement")}</p>
          ) : props.requests.length === 0 ? (
            <p data-testid="fonctionnalites-vide" className="px-5 py-5 text-sm text-gray-500">
              {t("console.fonctionnalites.vide")}
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {props.requests.map((request) => (
                <li
                  key={request.id}
                  data-testid={`demande-${request.id}`}
                  className="flex items-start gap-4 px-5 py-3.5"
                >
                  <div className="w-48 shrink-0">
                    <p className="text-sm font-medium text-gray-900">{request.requesterName}</p>
                    <p className="text-xs text-gray-500">
                      {request.requesterRole} · {request.organisationName ?? DASH}
                    </p>
                  </div>
                  <div className="w-44 shrink-0">
                    <p className="text-sm text-gray-700">{request.app}</p>
                    <p className="text-xs text-gray-500">
                      {fill("console.fonctionnalites.ecran", { ecran: request.screenLabel })}
                    </p>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-900">{request.goal}</p>
                    <p className="mt-1 text-xs text-gray-500">
                      <span className="font-medium">{t("console.fonctionnalites.manque")}</span>{" "}
                      {request.missing}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500">
                      <span className="font-medium">{t("console.fonctionnalites.permettrait")}</span>{" "}
                      {request.benefit}
                    </p>
                  </div>
                  <span className="w-28 shrink-0">
                    {request.screenshotUrl === null ? (
                      <span className="text-xs text-gray-500">{DASH}</span>
                    ) : (
                      <a
                        href={request.screenshotUrl}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="demande-capture"
                        className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700"
                      >
                        {t("console.fonctionnalites.captureJointe")}
                      </a>
                    )}
                  </span>
                  <span className="w-24 shrink-0 text-right text-xs text-gray-500">
                    {formatDate(new Date(request.submittedAt))}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function FonctionnalitesScreen(props: FonctionnalitesDoors): React.JSX.Element {
  const [requests, setRequests] = useState<readonly FeatureRequestView[] | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const { readFeatureRequests } = props;
  useEffect(() => {
    const run = { live: true };
    void (async () => {
      try {
        const answered = await readFeatureRequests();
        if (run.live) setRequests(answered);
      } catch (failure) {
        if (run.live)
          setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      }
    })();
    return () => {
      run.live = false;
    };
  }, [readFeatureRequests]);
  return <Fonctionnalites requests={requests} refusal={refusal} />;
}
